import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, date, pgEnum, boolean } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// Cross-domain imports
import { clientGroups, contacts, accounts } from "./schema-crm.js";
import { lenders } from "./schema-lenders.js"; // F002: FK reference for lenderId
import { lenderProducts } from "./schema-lenders.js"; // F004: FK for source_product_id

// ============================================================================
// DEALS MODULE TABLES (Deals, facilities, pipeline)
// ============================================================================

// 5. Deals Table (Finance deals - owned by applicants: contacts OR accounts)
export const dealTypeEnum = pgEnum("deal_type", ["equipment", "property", "working_capital", "residential", "business_acquisition", "smsf_finance", "development_finance", "other"]);

// Q9 Transaction Lifecycle Enums (PRD Q9 Design)
export const transactionStageEnum = pgEnum("transaction_stage_enum", ["scenario", "deal", "funded_loan"]);
export const scenarioSubstageEnum = pgEnum("scenario_substage_enum", [
  "initial_inquiry",
  "gathering_info",
  "seeking_indicatives",
  "preparing_proposal",
  "awaiting_decision"
]);
export const dealSubstageEnum = pgEnum("deal_substage_enum", [
  "application_prep",
  "submitted",
  "lender_assessment",
  "conditional_approval",
  "formal_approval",
  "settlement_booked",
  "settled"
]);

export const borrowerTypeEnum = pgEnum("borrower_type", ["contact", "account"]);
export const applicantTypeEnum = pgEnum("applicant_type", ["contact", "account"]);
export const applicantRoleEnum = pgEnum("applicant_role", ["primary_applicant", "co_applicant", "guarantor"]);
export const commissionStatusEnum = pgEnum("commission_status", ["expected", "invoiced", "paid"]);

export const sourceAtIntakeValues = [
  "website",
  "referral",
  "cold_call",
  "email_campaign",
  "social_media",
  "event",
  "partner",
  "existing_client",
  "other",
] as const;
export const sourceAtIntakeEnum = pgEnum("source_at_intake_enum", sourceAtIntakeValues);

export const financeReadinessStageValues = [
  "check_completed",
  "plan_delivered",
  "converted",
  "not_proceeding",
] as const;
export const financeReadinessStageEnum = pgEnum(
  "finance_readiness_stage_enum",
  financeReadinessStageValues,
);

export const conversionEventValues = [
  "application_lodged",
  "settled",
] as const;
export const conversionEventEnum = pgEnum("conversion_event_enum", conversionEventValues);

export const deals = pgTable("crm_x_deals", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealName: varchar("deal_name", { length: 255 }).notNull(),
  dealType: dealTypeEnum("deal_type").notNull(),

  // Q9 Transaction Lifecycle (PRD Q9 Design)
  transactionStage: transactionStageEnum("transaction_stage").notNull(),
  scenarioSubstage: scenarioSubstageEnum("scenario_substage"),
  dealSubstage: dealSubstageEnum("deal_substage"),

  // Q9 Transition Timestamps
  indicativeProposalAcceptedAt: timestamp("indicative_proposal_accepted_at"),
  dealStartedAt: timestamp("deal_started_at"),
  settlementCompletedAt: timestamp("settlement_completed_at"),

  amountRequested: numeric("amount_requested", { precision: 15, scale: 2 }),
  amountApproved: numeric("amount_approved", { precision: 15, scale: 2 }),
  amountSettled: numeric("amount_settled", { precision: 15, scale: 2 }).default("0"),
  lenderId: uuid("lender_id").references(() => lenders.id, { onDelete: "set null" }), // F002: FK to lenders (Sprint 7)
  lenderName: varchar("lender_name", { length: 255 }),
  expectedSettlementDate: date("expected_settlement_date"),
  actualSettlementDate: date("actual_settlement_date"),
  financeDate: date("finance_date"), // F010: Expected or actual date when deal funding is completed
  lodgedDate: date("lodged_date"),   // F010: Date when deal application was formally lodged with lender
  sourceAtIntake: sourceAtIntakeEnum("source_at_intake"),
  financeReadinessStage: financeReadinessStageEnum("finance_readiness_stage"),
  planDeliveredDate: date("plan_delivered_date"),
  bidSignoff: boolean("bid_signoff").default(false).notNull(),
  licenseeInitials: varchar("licensee_initials", { length: 20 }),
  bidSignedDate: date("bid_signed_date"),
  conversionEvent: conversionEventEnum("conversion_event"),
  conversionEventDate: date("conversion_event_date"),
  estimatedCommission: numeric("estimated_commission", { precision: 12, scale: 2 }),
  actualCommission: numeric("actual_commission", { precision: 12, scale: 2 }),
  commissionStatus: commissionStatusEnum("commission_status"),
  borrowerId: uuid("borrower_id").notNull(),
  borrowerType: borrowerTypeEnum("borrower_type").notNull(),
  clientGroupId: uuid("client_group_id").references(() => clientGroups.id),
  assignedTo: uuid("assigned_to"),
  probability: integer("probability").default(25),
  notes: text("notes"),
  organisationId: uuid("organisation_id"),
  isCanary: boolean("is_canary").default(false).notNull(),
  assignedBrokerId: uuid("assigned_broker_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// 6. Deal Applicants Table (Many-to-many relationship between deals and contacts/accounts)
// LEGACY: Being replaced by dealParties (polymorphic Q7 design)
export const dealApplicants = pgTable("crm_x_deal_applicants", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  applicantId: uuid("applicant_id").notNull(),
  applicantType: applicantTypeEnum("applicant_type").notNull(),
  applicantRole: applicantRoleEnum("applicant_role").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// 6a. Deal Parties Table (Q7 Polymorphic party junction - replaces dealApplicants)
export const dealPartyRoleEnum = pgEnum("deal_party_role_enum", [
  "borrower",
  "guarantor",
  "authorised_signatory",
  "primary_contact",
  "secondary_contact",
  "associated_person"
]);

export const dealParties = pgTable("crm_x_deal_parties", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  partyId: uuid("party_id").notNull(),
  partyType: varchar("party_type", { length: 20 }).notNull(),
  role: dealPartyRoleEnum("role").notNull(),
  source: varchar("source", { length: 30 }).default("manual").notNull(),
  sourceFacilityId: uuid("source_facility_id"),
  status: varchar("status", { length: 20 }).default("active").notNull(),
  associatedAccountId: uuid("associated_account_id"),
  associationRole: varchar("association_role", { length: 50 }),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// 6b. Facilities Table (Individual loan facilities within a deal - multi-facility structuring)
// v2.0: Extended schema with interest rates, term details, and repayment calculations (D-003)
export const loanTypeEnum = pgEnum("loan_type_enum", [
  "home_loan",
  "commercial_loan",
  "equipment_finance",
  "business_loan",
  "property_development",
  "vehicle_finance"
]);

export const facilityStatusEnum = pgEnum("facility_status_enum", [
  "indicative",
  "submitted",
  "approved",
  "settled",
  "declined",
  "withdrawn",
  "scenario_not_proceeding" // v2.0: Added for scenario promotion workflow
]);

export const paymentFrequencyEnum = pgEnum("payment_frequency_enum", [
  "weekly",
  "fortnightly",
  "monthly",
  "quarterly",
  "annually"
]);

// F004 (SPRINT-30): Overall repayment structure for the facility.
// Declared here (not schema-facility-pricing.ts) to avoid circular imports —
// schema-facility-pricing.ts imports from schema-deals.ts.
export const facilityRepaymentTypeEnum = pgEnum("facility_repayment_type_enum", [
  "interest_only",
  "principal_and_interest",
  "interest_only_then_pi",
]);

export const facilities = pgTable("crm_x_facilities", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  facilityName: varchar("facility_name", { length: 255 }).notNull(),
  facilityType: varchar("facility_type", { length: 50 }).notNull().default("standard"),
  facilityStatus: facilityStatusEnum("facility_status").default("indicative"),
  loanType: loanTypeEnum("loan_type"),

  // Financial fields (BR-002 aggregation sources)
  requestedAmount: numeric("requested_amount", { precision: 15, scale: 2 }),
  approvedLimit: numeric("approved_limit", { precision: 15, scale: 2 }), // v2.0: Renamed from approvedAmount
  fundedAmount: numeric("funded_amount", { precision: 15, scale: 2 }), // v2.0: Renamed from settledAmount

  // Lender and commission (D-001)
  lenderId: uuid("lender_id"),
  lenderProductId: uuid("lender_product_id"),
  customCommissionRate: numeric("custom_commission_rate", { precision: 5, scale: 4 }),

  // Interest rate fields (D-003)
  baseRate: numeric("base_rate", { precision: 8, scale: 6 }),
  margin: numeric("margin", { precision: 8, scale: 6 }),
  totalRate: numeric("total_rate", { precision: 8, scale: 6 }), // GENERATED COLUMN in database

  // Term fields (D-003)
  termMonths: integer("term_months"),
  ioMonths: integer("io_months"),
  amortisationPeriodMonths: integer("amortisation_period_months"), // F011: Override for P&I calc (commercial loans)
  repaymentFrequency: paymentFrequencyEnum("repayment_frequency").default("monthly"),

  // Repayment calculations (D-003)
  ioRepayment: numeric("io_repayment", { precision: 15, scale: 2 }), // GENERATED COLUMN in database
  piRepayment: numeric("pi_repayment", { precision: 15, scale: 2 }), // Trigger-calculated
  repaymentOverride: numeric("repayment_override", { precision: 15, scale: 2 }),

  // Date fields
  applicationDate: date("application_date"),
  approvalDate: date("approval_date"),
  settlementDate: date("settlement_date"),
  maturityDate: date("maturity_date"),

  // F004 (SPRINT-30): Parent/variant relationship — self-referential FK.
  // No .references() here to avoid Drizzle circular self-ref issues at schema init;
  // the FK constraint is enforced by the SQL migration.
  parentFacilityId: uuid("parent_facility_id"),

  // F004 (SPRINT-30): Pointer to the current active pricing schedule for this facility.
  // currentPricingScheduleId references crm_x_facility_pricing_schedules(id).
  // We use uuid() without .references() to avoid circular imports between
  // schema-deals.ts and schema-facility-pricing.ts. The FK is in the SQL migration.
  currentPricingScheduleId: uuid("current_pricing_schedule_id"),

  // F004 (SPRINT-30): Lender product this facility was priced from.
  sourceProductId: uuid("source_product_id").references(() => lenderProducts.id, { onDelete: "set null" }),

  // F004 (SPRINT-30): Overall repayment structure (trigger-populated, nullable).
  repaymentType: facilityRepaymentTypeEnum("repayment_type"),
  balloonAmount: numeric("balloon_amount", { precision: 15, scale: 2 }),
  balloonPct: numeric("balloon_pct", { precision: 5, scale: 2 }),

  // F004 (SPRINT-30): Key milestone dates.
  financeClauseDate: date("finance_clause_date"),
  expectedSettlementDate: date("expected_settlement_date"),
  actualSettlementDate: date("actual_settlement_date"),

  // Audit fields
  organisationId: uuid("organisation_id"),
  assignedBrokerId: uuid("assigned_broker_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

export const facilityRepayments = pgTable("crm_x_facility_repayments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  facilityId: uuid("facility_id").references(() => facilities.id, { onDelete: "cascade" }).notNull(),
  repaymentType: varchar("repayment_type", { length: 20 }).notNull(),
  repaymentFrequency: varchar("repayment_frequency", { length: 20 }),
  repaymentAmount: numeric("repayment_amount", { precision: 12, scale: 2 }),
  startDate: date("start_date"),
  endDate: date("end_date"),
  termMonths: integer("term_months"),
  balloonAmount: numeric("balloon_amount", { precision: 15, scale: 2 }),
  balloonPct: numeric("balloon_pct", { precision: 8, scale: 6 }),
  balloonDate: date("balloon_date"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 6c. Facility Parties Table (SPRINT-3: Party management at facility level)
export const facilityPartyRoleEnum = pgEnum("facility_party_role_enum", [
  "borrower",
  "co_borrower",
  "guarantor",
  "trustee",
  "beneficiary",
  "director",
  "shareholder",
  "authorised_signatory",
  "security_provider",
  "mortgagor"
]);

export const facilityParties = pgTable("crm_x_facility_parties", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  facilityId: uuid("facility_id").references(() => facilities.id, { onDelete: "cascade" }).notNull(),
  partyType: varchar("party_type", { length: 20 }).notNull(), // 'account' or 'contact'
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "cascade" }),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }),
  partyRole: facilityPartyRoleEnum("party_role").notNull(),
  ownershipPercentage: numeric("ownership_percentage", { precision: 5, scale: 2 }),
  isPrimary: boolean("is_primary").default(false),
  guaranteeId: uuid("guarantee_id"),
  status: varchar("status", { length: 20 }).default("active"),
  effectiveDate: date("effective_date"),
  endDate: date("end_date"),
  notes: text("notes"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// 7. CRM Tasks Table (ClickUp task metadata for synchronization)
export const crmTasks = pgTable("crm_x_crm_tasks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  clickupTaskId: varchar("clickup_task_id", { length: 50 }).unique().notNull(),
  taskName: varchar("task_name", { length: 500 }),
  taskStatus: varchar("task_status", { length: 50 }),
  dueDate: date("due_date"),
  assignee: varchar("assignee", { length: 255 }),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "set null" }),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "set null" }),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "set null" }),
  lastSyncedAt: timestamp("last_synced_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// 7a. Deal Tasks Table (F008: Native deal task management - separate from ClickUp sync)
export const dealTaskStatusEnum = pgEnum("deal_task_status_enum", ["pending", "in_progress", "completed"]);
export const dealTaskPriorityEnum = pgEnum("deal_task_priority_enum", ["low", "medium", "high", "urgent"]);

export const dealTasks = pgTable("crm_x_deal_tasks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  title: varchar("title", { length: 500 }).notNull(),
  description: text("description"),
  status: dealTaskStatusEnum("status").default("pending").notNull(),
  priority: dealTaskPriorityEnum("priority").default("medium").notNull(),
  dueDate: date("due_date"),
  startDate: date("start_date"),
  assignedTo: uuid("assigned_to"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
});

// 8. Deal Pipeline Stage History Table (Q9 Transaction Lifecycle Audit Trail)
// Scenarios are now just deals with transaction_stage='scenario' (Q9 design)
export const dealPipelineStageHistory = pgTable("crm_x_deal_pipeline_stage_history", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),

  // Stage Transitions
  fromTransactionStage: transactionStageEnum("from_transaction_stage"),
  toTransactionStage: transactionStageEnum("to_transaction_stage"),
  fromScenarioSubstage: scenarioSubstageEnum("from_scenario_substage"),
  toScenarioSubstage: scenarioSubstageEnum("to_scenario_substage"),
  fromDealSubstage: dealSubstageEnum("from_deal_substage"),
  toDealSubstage: dealSubstageEnum("to_deal_substage"),

  // Audit
  changedBy: uuid("changed_by"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
  reason: text("reason"),
  systemGenerated: boolean("system_generated").default(false),
  actorKind: text("actor_kind"),

  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const dateOnlySchema = z.string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD")
  .refine((value) => {
    const [year, month, day] = value.split("-").map(Number);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return parsed.getUTCFullYear() === year &&
      parsed.getUTCMonth() === month - 1 &&
      parsed.getUTCDate() === day;
  }, "Date must be a real calendar date");

const nullableDateOnlySchema = z.preprocess(
  (value) => value === "" ? null : value,
  dateOnlySchema.nullable().optional(),
);

const insertDealBaseSchema = createInsertSchema(deals).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  isCanary: true,
}).extend({
  amountRequested: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  amountApproved: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  estimatedCommission: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  actualCommission: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  lenderId: z.string().uuid().nullable().optional(), // F002: FK to lenders (Sprint 7)
  financeDate: z.string().nullable().optional().transform(val => {
    if (!val || val.trim() === "") return null;
    return val.trim();
  }),
  lodgedDate: z.string().nullable().optional().transform(val => {
    if (!val || val.trim() === "") return null;
    return val.trim();
  }),
  planDeliveredDate: nullableDateOnlySchema,
  bidSignedDate: nullableDateOnlySchema,
  conversionEventDate: nullableDateOnlySchema,
});

export const insertDealSchema = insertDealBaseSchema;
export const validatedInsertDealSchema = insertDealBaseSchema.superRefine((data, ctx) => {
  if (data.bidSignoff && (!data.licenseeInitials?.trim() || !data.bidSignedDate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "licenseeInitials and bidSignedDate are required when bidSignoff is true",
      path: ["bidSignoff"],
    });
  }
});
export const updateDealSchema = insertDealBaseSchema.partial(); // createdBy already omitted
export const selectDealSchema = createSelectSchema(deals);

export const insertDealApplicantSchema = createInsertSchema(dealApplicants).omit({
  id: true,
  createdAt: true
});
export const updateDealApplicantSchema = insertDealApplicantSchema.partial();
export const selectDealApplicantSchema = createSelectSchema(dealApplicants);

// Deal Parties Schemas (Q7 Polymorphic Design - replaces dealApplicants)
export const insertDealPartySchema = createInsertSchema(dealParties).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true
});
export const updateDealPartySchema = insertDealPartySchema.partial();
export const selectDealPartySchema = createSelectSchema(dealParties);

export const insertFacilitySchema = createInsertSchema(facilities).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true
}).extend({
  facilityType: z.string().optional().default("standard"),
  requestedAmount: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  approvedLimit: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  fundedAmount: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
})
// ISSUE-118: Progressive field validation for deals and facilities
// Refine 1: lenderId required when submitted/approved/settled
.refine((data) => {
  const requiresLender = ["submitted", "approved", "settled"].includes(data.facilityStatus || "");
  if (requiresLender && !data.lenderId) {
    return false;
  }
  return true;
}, {
  message: "Lender is required when facility status is 'submitted', 'approved', or 'settled'",
  path: ["lenderId"],
})
// Refine 2: approvedLimit required when approved/settled
.refine((data) => {
  const requiresApproved = ["approved", "settled"].includes(data.facilityStatus || "");
  if (requiresApproved && !data.approvedLimit) {
    return false;
  }
  return true;
}, {
  message: "Approved Limit is required when facility status is 'approved' or 'settled'",
  path: ["approvedLimit"],
})
// Refine 3: approvalDate required when approved/settled
.refine((data) => {
  const requiresApprovalDate = ["approved", "settled"].includes(data.facilityStatus || "");
  if (requiresApprovalDate && !data.approvalDate) {
    return false;
  }
  return true;
}, {
  message: "Approval Date is required when facility status is 'approved' or 'settled'",
  path: ["approvalDate"],
})
// Refine 4: fundedAmount required when settled (ISSUE-119 pattern)
.refine((data) => {
  if (data.facilityStatus === "settled" && !data.fundedAmount) {
    return false;
  }
  return true;
}, {
  message: "Funded Amount is required when facility status is 'settled'",
  path: ["fundedAmount"],
})
// Refine 5: settlementDate required when settled
.refine((data) => {
  if (data.facilityStatus === "settled" && !data.settlementDate) {
    return false;
  }
  return true;
}, {
  message: "Settlement Date is required when facility status is 'settled'",
  path: ["settlementDate"],
})
// F005: Refine 6: requestedAmount must not be negative
.refine((data) => {
  if (data.requestedAmount !== null && data.requestedAmount !== undefined) {
    const amount = parseFloat(data.requestedAmount);
    if (!isNaN(amount) && amount < 0) {
      return false;
    }
  }
  return true;
}, {
  message: "Requested amount cannot be negative",
  path: ["requestedAmount"],
})
// F005: Refine 7: approvedLimit must not be negative
.refine((data) => {
  if (data.approvedLimit !== null && data.approvedLimit !== undefined) {
    const amount = parseFloat(data.approvedLimit);
    if (!isNaN(amount) && amount < 0) {
      return false;
    }
  }
  return true;
}, {
  message: "Approved limit cannot be negative",
  path: ["approvedLimit"],
})
// F005: Refine 8: fundedAmount must not be negative
.refine((data) => {
  if (data.fundedAmount !== null && data.fundedAmount !== undefined) {
    const amount = parseFloat(data.fundedAmount);
    if (!isNaN(amount) && amount < 0) {
      return false;
    }
  }
  return true;
}, {
  message: "Funded amount cannot be negative",
  path: ["fundedAmount"],
})
// F005: Refine 9: baseRate must be between 0 and 1 (decimal, not percentage)
.refine((data) => {
  if (data.baseRate !== null && data.baseRate !== undefined) {
    const rate = parseFloat(String(data.baseRate));
    if (!isNaN(rate) && (rate > 1 || rate < 0)) {
      return false;
    }
  }
  return true;
}, {
  message: "Base rate must be a decimal between 0 and 1 (e.g., 0.05 for 5%), not a percentage",
  path: ["baseRate"],
})
// F005: Refine 10: margin must be between 0 and 1 (decimal, not percentage)
.refine((data) => {
  if (data.margin !== null && data.margin !== undefined) {
    const rate = parseFloat(String(data.margin));
    if (!isNaN(rate) && rate > 1) {
      return false;
    }
  }
  return true;
}, {
  message: "Margin must be a decimal between 0 and 1 (e.g., 0.02 for 2%), not a percentage",
  path: ["margin"],
})
// F005: Refine 11: termMonths must be integer if provided
.refine((data) => {
  if (data.termMonths !== null && data.termMonths !== undefined) {
    if (!Number.isInteger(data.termMonths)) {
      return false;
    }
  }
  return true;
}, {
  message: "Term months must be a whole number (integer)",
  path: ["termMonths"],
})
// F005: Refine 12: ioMonths must be integer if provided
.refine((data) => {
  if (data.ioMonths !== null && data.ioMonths !== undefined) {
    if (!Number.isInteger(data.ioMonths)) {
      return false;
    }
  }
  return true;
}, {
  message: "IO months must be a whole number (integer)",
  path: ["ioMonths"],
})
// F009: Refine 13: termMonths must be positive if provided
.refine((data) => {
  if (data.termMonths !== null && data.termMonths !== undefined) {
    if (data.termMonths < 0) {
      return false;
    }
  }
  return true;
}, {
  message: "Term months cannot be negative",
  path: ["termMonths"],
})
// F009: Refine 14: ioMonths must not exceed termMonths
.refine((data) => {
  if (data.ioMonths !== null && data.ioMonths !== undefined &&
      data.termMonths !== null && data.termMonths !== undefined) {
    if (data.ioMonths > data.termMonths) {
      return false;
    }
  }
  return true;
}, {
  message: "I/O period cannot exceed loan term",
  path: ["ioMonths"],
})
// F011: Refine 15: amortisationPeriodMonths must be positive integer if provided
.refine((data) => {
  if (data.amortisationPeriodMonths !== null && data.amortisationPeriodMonths !== undefined) {
    if (!Number.isInteger(data.amortisationPeriodMonths) || data.amortisationPeriodMonths <= 0) {
      return false;
    }
  }
  return true;
}, {
  message: "Amortisation period must be a positive integer",
  path: ["amortisationPeriodMonths"],
});
export const updateFacilitySchema = createInsertSchema(facilities).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true
}).extend({
  requestedAmount: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  approvedLimit: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  fundedAmount: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
}).partial()
// F005: Refine - requestedAmount must not be negative
.refine((data) => {
  if (data.requestedAmount !== null && data.requestedAmount !== undefined) {
    const amount = parseFloat(data.requestedAmount);
    if (!isNaN(amount) && amount < 0) {
      return false;
    }
  }
  return true;
}, {
  message: "Requested amount cannot be negative",
  path: ["requestedAmount"],
})
// F005: Refine - approvedLimit must not be negative
.refine((data) => {
  if (data.approvedLimit !== null && data.approvedLimit !== undefined) {
    const amount = parseFloat(data.approvedLimit);
    if (!isNaN(amount) && amount < 0) {
      return false;
    }
  }
  return true;
}, {
  message: "Approved limit cannot be negative",
  path: ["approvedLimit"],
})
// F005: Refine - fundedAmount must not be negative
.refine((data) => {
  if (data.fundedAmount !== null && data.fundedAmount !== undefined) {
    const amount = parseFloat(data.fundedAmount);
    if (!isNaN(amount) && amount < 0) {
      return false;
    }
  }
  return true;
}, {
  message: "Funded amount cannot be negative",
  path: ["fundedAmount"],
})
// F005: Refine - baseRate must be between 0 and 1 (decimal, not percentage)
.refine((data) => {
  if (data.baseRate !== null && data.baseRate !== undefined) {
    const rate = parseFloat(String(data.baseRate));
    if (!isNaN(rate) && (rate > 1 || rate < 0)) {
      return false;
    }
  }
  return true;
}, {
  message: "Base rate must be a decimal between 0 and 1 (e.g., 0.05 for 5%), not a percentage",
  path: ["baseRate"],
})
// F005: Refine - margin must be between 0 and 1 (decimal, not percentage)
.refine((data) => {
  if (data.margin !== null && data.margin !== undefined) {
    const rate = parseFloat(String(data.margin));
    if (!isNaN(rate) && (rate > 1 || rate < 0)) {
      return false;
    }
  }
  return true;
}, {
  message: "Margin must be a decimal between 0 and 1 (e.g., 0.02 for 2%), not a percentage",
  path: ["margin"],
})
// F005: Refine - termMonths must be integer if provided
.refine((data) => {
  if (data.termMonths !== null && data.termMonths !== undefined) {
    if (!Number.isInteger(data.termMonths)) {
      return false;
    }
  }
  return true;
}, {
  message: "Term months must be a whole number (integer)",
  path: ["termMonths"],
})
// F005: Refine - ioMonths must be integer if provided
.refine((data) => {
  if (data.ioMonths !== null && data.ioMonths !== undefined) {
    if (!Number.isInteger(data.ioMonths)) {
      return false;
    }
  }
  return true;
}, {
  message: "IO months must be a whole number (integer)",
  path: ["ioMonths"],
})
// F009: Refine - termMonths must be positive if provided
.refine((data) => {
  if (data.termMonths !== null && data.termMonths !== undefined) {
    if (data.termMonths < 0) {
      return false;
    }
  }
  return true;
}, {
  message: "Term months cannot be negative",
  path: ["termMonths"],
})
// F009: Refine - ioMonths must not exceed termMonths
.refine((data) => {
  if (data.ioMonths !== null && data.ioMonths !== undefined &&
      data.termMonths !== null && data.termMonths !== undefined) {
    if (data.ioMonths > data.termMonths) {
      return false;
    }
  }
  return true;
}, {
  message: "I/O period cannot exceed loan term",
  path: ["ioMonths"],
})
// F011: Refine - amortisationPeriodMonths must be positive integer if provided
.refine((data) => {
  if (data.amortisationPeriodMonths !== null && data.amortisationPeriodMonths !== undefined) {
    if (!Number.isInteger(data.amortisationPeriodMonths) || data.amortisationPeriodMonths <= 0) {
      return false;
    }
  }
  return true;
}, {
  message: "Amortisation period must be a positive integer",
  path: ["amortisationPeriodMonths"],
});
export const selectFacilitySchema = createSelectSchema(facilities);

export const insertFacilityPartySchema = createInsertSchema(facilityParties).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true
});
export const updateFacilityPartySchema = insertFacilityPartySchema.partial();
export const selectFacilityPartySchema = createSelectSchema(facilityParties);

export const insertCrmTaskSchema = createInsertSchema(crmTasks).omit({
  id: true,
  createdAt: true,
  lastSyncedAt: true
});
export const updateCrmTaskSchema = insertCrmTaskSchema.partial(); // No createdBy field
export const selectCrmTaskSchema = createSelectSchema(crmTasks);

// F008: Deal Tasks Schemas (native deal task management)
export const insertDealTaskSchema = createInsertSchema(dealTasks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).extend({
  // Ensure title is not just whitespace
  title: z.string().min(1, "Title is required").max(500, "Title must be 500 characters or less"),
});
export const updateDealTaskSchema = insertDealTaskSchema.partial();
export const selectDealTaskSchema = createSelectSchema(dealTasks);

// NOTE: Scenarios table removed - scenarios are now deals with transaction_stage='scenario' (Q9 Design)
// Use deal schemas with transactionStage filter instead
export const insertScenarioSchema = insertDealSchema;
export const updateScenarioSchema = updateDealSchema;
export const selectScenarioSchema = selectDealSchema;

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type InsertDeal = z.infer<typeof insertDealSchema>;
export type UpdateDeal = z.infer<typeof updateDealSchema>;
export type Deal = typeof deals.$inferSelect;

export type InsertDealApplicant = z.infer<typeof insertDealApplicantSchema>;
export type UpdateDealApplicant = z.infer<typeof updateDealApplicantSchema>;
export type DealApplicant = typeof dealApplicants.$inferSelect;

export type InsertDealParty = z.infer<typeof insertDealPartySchema>;
export type UpdateDealParty = z.infer<typeof updateDealPartySchema>;
export type DealParty = typeof dealParties.$inferSelect;

export type InsertFacility = z.infer<typeof insertFacilitySchema>;
export type UpdateFacility = z.infer<typeof updateFacilitySchema>;
export type Facility = typeof facilities.$inferSelect;

export type InsertFacilityParty = z.infer<typeof insertFacilityPartySchema>;
export type UpdateFacilityParty = z.infer<typeof updateFacilityPartySchema>;
export type FacilityParty = typeof facilityParties.$inferSelect;

export type InsertCrmTask = z.infer<typeof insertCrmTaskSchema>;
export type UpdateCrmTask = z.infer<typeof updateCrmTaskSchema>;
export type CrmTask = typeof crmTasks.$inferSelect;

// F008: Deal Task Types
export type InsertDealTask = z.infer<typeof insertDealTaskSchema>;
export type UpdateDealTask = z.infer<typeof updateDealTaskSchema>;
export type DealTask = typeof dealTasks.$inferSelect;

export type InsertScenario = z.infer<typeof insertScenarioSchema>;
export type UpdateScenario = z.infer<typeof updateScenarioSchema>;
// NOTE: Scenarios are now deals with transaction_stage='scenario' - using Deal type
export type Scenario = Deal;
